import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LinearProgress } from '@mui/material';

import { useSnackbar } from 'notistack';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';

import {
  FlightEventFlightDetailsLookup, DispatchEventReport, FlightLookup, EventReport, DispatchEventReportDTO,
  ReportType,
} from '../../core/api/clients';

import FlightLookupForm from '../../components/FlightLookupForm';
import DispatchEventNewOptions from './DispatchEventNewOptions';
import useCreateNewDispatchEvent from '../../core/api/hooks/dispatch-event/useCreateNewDispatchEvent';
import useCreateNewDispatchEventFromExisting from '../../core/api/hooks/dispatch-event/useCreateNewDispatchEventFromExisting';
import useCreateNewIncident from '../../core/api/hooks/incident/useCreateNewIncident';
import useGetEventReportsByType from '../../core/api/hooks/event-report/useGetEventReportsByType';

function DispatchEventFormNew() {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmit, setIsSubmit] = React.useState<boolean>(false);
  const [cssCreate, setCssCreate] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [flightSegments, setFlightSegments] = React.useState<DispatchEventReport[]>([]);
  const authorizedFormData = useAuthorizedFormData();
  const { mutate: createDispatchEvent, status: createDispatchEventStatus } = useCreateNewDispatchEvent();
  const { mutate: createFromExisting } = useCreateNewDispatchEventFromExisting();
  const { mutate: createIncident } = useCreateNewIncident();
  const { data: reports } = useGetEventReportsByType({
    reportType: ReportType.SafetyEventReport,
    hours: 24,
  });
  const handleCreateDispatchEvent = (flightEventDetailsLookup: FlightEventFlightDetailsLookup) => {
    createDispatchEvent(flightEventDetailsLookup, {
      onSuccess: (dispatchEventReports: DispatchEventReport[]) => {
        if (dispatchEventReports.length === 1) {
          navigate(`${authorizedFormData.metadata?.route}/${dispatchEventReports[0].id}/edit`, { replace: true });
        } else {
          setFlightSegments(dispatchEventReports);
        }
      },
      onError: () => enqueueSnackbar('Flight not found', {
        variant: 'warning',
      }),
    });
  };
  const handleCreateIncident = (eventReports: EventReport[], onSuccess: () => void, onError: () => void) => {
    if (eventReports && !eventReports[0].incidentId) {
      createIncident(eventReports.map((eventReport) => ({
        incidentId: undefined,
        eventReportId: eventReport.id,
        incidentConcurrencyId: undefined,
        eventReportConcurrencyId: eventReport.concurrencyId,
      })), {
        onSuccess: () => {
          onSuccess();
        },
        onError: () => {
          onError();
        },
      });
    } else {
      onSuccess();
    }
  };
  const handleCreateDispatchEventReportFromExisting = (eventReport: EventReport) => {
    const error = () => enqueueSnackbar('Unable to create dispatch event from existing', {
      variant: 'warning',
    });
    createFromExisting(eventReport, {
      onSuccess: (dto: DispatchEventReportDTO) => {
        if (!dto.error && dto.eventReport) {
          handleCreateIncident(
            [dto.eventReport, eventReport],
            () => navigate(`${authorizedFormData.metadata?.route}/${dto.eventReport!.id}/edit`, { replace: true }),
            error,
          );
        }
      },
      onError: error,
    });
  };
  React.useEffect(() => {
    if (searchParams.has('cssLegId')) {
      const cssLegId = searchParams.get('cssLegId');
      if (cssLegId !== null) {
        setCssCreate(true);
        handleCreateDispatchEvent({ cssLegId });
      }
    }
  }, [searchParams]);

  const onSubmit = (flightLookup: FlightLookup) => {
    setIsSubmit(true);
    handleCreateDispatchEvent({ flightLookup });
  };
  const onCancel = () => navigate(authorizedFormData.metadata?.route ?? '');
  const onBlank = () => {
    setIsSubmit(false);
    handleCreateDispatchEvent({});
  };

  if (cssCreate && createDispatchEventStatus === 'pending') {
    return (
      <LinearProgress />
    );
  }

  return (
    <React.Fragment>
      <FlightLookupForm
        onSubmit={onSubmit}
        onCancel={onCancel}
        onBlank={onBlank}
        color={authorizedFormData.metadata?.color}
        loading={isSubmit && createDispatchEventStatus === 'pending'}
        isDateDefault
        reports={reports}
        onSubmitFromExisting={handleCreateDispatchEventReportFromExisting}
      />
      <DispatchEventNewOptions
        data={flightSegments}
      />
    </React.Fragment>
  );
}
export default DispatchEventFormNew;
